import React, { useState } from 'react';
import { Button, Currency, NavigationItem, Spinner } from 'ui-toolkit';
import { InlineEditFieldContainer } from '../../editSimpleField/InlineEditFieldContainer';
import * as yup from 'yup';
import { Form, Formik } from 'formik';
import { TransactionValidationSchemaGenerator } from '../../overview/EditTransactionPrice/TransactionValidationSchema';
import { DarkFaEdit } from '../../overview/Summary/DarkFaEdit';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { CardFlexRowTW, StyledCardDivTW, StyledCardTextTW } from './feesOverviewCardsStylesTW';
import { CardTW } from '../../../../tailwindComponents/CardTW/CardTW';
import { HeaderText3TW } from '../../../../tailwindComponents/HeaderTextTW/HeaderTextTW';
import { BodyTextTW } from '../../../../tailwindComponents/bodyText/BodyTextTW';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { eTransactionCategory } from '../../../../ui-toolkit/enums/eTransactionType';
import { ITransaction } from '../../../../ui-toolkit/orders/interfaces/ITransaction';

export interface ILoanDetailsCard {
  loanAmount: number | null;
  loanNumber?: string | null;
  purchasePrice?: number | null;
  transactionType: eTransactionCategory;
  onUpdate: (values: any) => void;
  updateStatus: QueryStatus;
}

const EditWarning = ' Updates will result in regeneration of applicable documents and fees.';

export const LoanDetailsCard: React.FC<ILoanDetailsCard> = ({
  loanAmount,
  loanNumber,
  purchasePrice,
  transactionType,
  onUpdate,
  updateStatus,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const getValidationSchema = (field: keyof ITransaction) =>
    TransactionValidationSchemaGenerator(transactionType, field);

  return (
    <Formik
      initialValues={{
        loan_number: loanNumber,
        loan_amount: loanAmount,
        purchase_price: purchasePrice,
      }}
      validationSchema={yup.object({
        loan_number: getValidationSchema('loan_number'),
        loan_amount: getValidationSchema('loan_amount'),
        purchase_price: getValidationSchema('purchase_price'),
      })}
      onSubmit={async (values) => {
        await onUpdate(values);
        setIsEditing(false);
      }}
    >
      {({ isSubmitting }) => (
        <Form
          className={classNames(
            transactionType === eTransactionCategory.Refinance ? 'basis-72' : 'basis-80',
            'shrink-0 grow-0'
          )}
        >
          <CardTW contentClassName="p-0 pt-1 pb-4">
            <CardFlexRowTW className={'flex-end items-center justify-between pb-0 pl-4'}>
              {!isEditing && (
                <>
                  <HeaderText3TW className={'pt-3'}>Loan Details</HeaderText3TW>
                  <div className={'flex flex-row'}>
                    <NavigationItem text={`Edit Loan Details`} onClick={() => setIsEditing(!isEditing)}>
                      <DarkFaEdit />
                    </NavigationItem>
                  </div>
                </>
              )}
            </CardFlexRowTW>
            <CardFlexRowTW data-test-id={'loan-number'} className={'items-start justify-between pl-4'}>
              <InlineEditFieldContainer
                field={'loan_number'}
                label={<div>Loan Number: {loanNumber || 'None'}</div>}
                isEditing={isEditing}
              >
                <div> </div>
              </InlineEditFieldContainer>
              {isEditing && !isSubmitting && (
                <div className={'flex flex-row'}>
                  <Button intent="ghost" isInline type="submit">
                    Update
                  </Button>
                  <NavigationItem text={`Edit Loan Details`} onClick={() => setIsEditing(!isEditing)}>
                    <DarkFaEdit />
                  </NavigationItem>
                </div>
              )}
              {updateStatus === QueryStatus.pending && <Spinner />}
            </CardFlexRowTW>
            <div className={'flex flex-row'}>
              <StyledCardDivTW data-test-id={'loan-amount'} className={'flex-grow basis-1/2'}>
                <InlineEditFieldContainer field={'loan_amount'} label={'Loan Amount'} isEditing={isEditing}>
                  <StyledCardTextTW>{loanAmount ? <Currency value={loanAmount} /> : 'None'}</StyledCardTextTW>
                </InlineEditFieldContainer>
              </StyledCardDivTW>
              {transactionType === 'purchase' && (
                <StyledCardDivTW className={'basis-1/2'}>
                  <InlineEditFieldContainer field={'purchase_price'} label={'Purchase Price'} isEditing={isEditing}>
                    <StyledCardTextTW>{purchasePrice ? <Currency value={purchasePrice} /> : 'None'}</StyledCardTextTW>
                  </InlineEditFieldContainer>
                </StyledCardDivTW>
              )}
            </div>
            {isEditing && (
              <BodyTextTW className={'flex-wrap px-4 pt-2'}>
                <FontAwesomeIcon icon={faTriangleExclamation} />
                {EditWarning}
              </BodyTextTW>
            )}
          </CardTW>
        </Form>
      )}
    </Formik>
  );
};
